import { useApiQuery } from '@/api/main/query'
import {
  fetchBalancesFailure,
  fetchBalancesStart,
  fetchBalancesSuccess,
  TraderBalances,
} from '@/entities/balances'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { useToast } from '@/shared/hooks'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { useEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'

const BALANCE_ENDPOINTS: Partial<Record<Roles, string>> = {
  [Roles.Trader]: '/v1/trader/balance',
  [Roles.Merchant]: '/v1/merchant/balance',
} as const

export const useBalances = () => {
  const dispatch = useDispatch()
  const { addToast } = useToast()
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)

  const endpoint = userRole ? BALANCE_ENDPOINTS[userRole] : null
  const shouldFetch = !!endpoint
  const queryKey = useMemo(() => ['balances', userRole], [userRole])

  const { data: dataBalances, error: errorBalances } = useApiQuery<TraderBalances[]>(
    queryKey,
    endpoint || '',
    undefined,
    { enabled: shouldFetch }
  )

  // Эффект для старта загрузки
  useEffect(() => {
    if (!shouldFetch) return
    dispatch(fetchBalancesStart())
  }, [dispatch, shouldFetch])

  // Эффект для обработки ошибок
  useEffect(() => {
    if (!shouldFetch || !errorBalances) return
    const key = 'balances-error-handled'
    if (sessionStorage.getItem(key) === errorBalances.message) return
    sessionStorage.setItem(key, errorBalances.message)
    dispatch(fetchBalancesFailure(errorBalances.message))
    addToast('Ошибка!', errorBalances.message, 'error')
  }, [errorBalances, dispatch, addToast, shouldFetch])

  // Эффект для успешной загрузки
  useEffect(() => {
    if (!shouldFetch || !dataBalances) return
    sessionStorage.removeItem('balances-error-handled')
    dispatch(fetchBalancesSuccess(dataBalances))
  }, [dataBalances, dispatch, shouldFetch])

  return {
    dataBalances,
    errorBalances,
    userRole,
    isLoading: shouldFetch && !dataBalances && !errorBalances,
  }
}
